<template>
  <div class="audio-sound-wrapper">
    <div class="audio-sound-player">
      <v-slider
        v-model="progress"
        v-on:click="setPosition"
        small
      >
        <template v-slot:prepend>
          <v-icon
            @click="pause()"
            color="white"
            v-if="isPaused"
          >
            mdi-play
          </v-icon>
          <v-icon
            @click="pause()"
            color="white"
            v-else
          >
            mdi-pause
          </v-icon>
          <v-icon
            @click="mute()"
            color="white"
            v-if="!isMuted"
          >
            mdi-volume-high
          </v-icon>
          <v-icon
            @click="mute()"
            color="white"
            v-else
          >
            mdi-volume-off
          </v-icon>
        </template>
        <template v-slot:append>
          <div class="audio-sound-timer">
            <span>{{ currentTime }} / {{ duration }}</span>
          </div>
        </template>
      </v-slider>
    </div>
    <audio
      :loop="innerLoop"
      ref="audiofile"
      :src="file"
      preload="auto"
      autoplay
      style="display:none;"></audio>
  </div>
</template>
<script lang="js">
import moment from 'moment'
const BASE_VOLUME = 7.5
export default {
  name: 'vue-audio',
  props: [
    'file',
    'audioTitle',
    'bookTitle',
    'chapter',
    'section',
    'autoPlay',
    'loop'
  ],
  computed: {
    duration: function () {
      return this.audio ? this.convertTimeHHMMSS(this.totalDuration) : ''
    }
  },
  data () {
    return {
      isMuted: false,
      loaded: false,
      isPaused: false,
      progress: 0,
      progressStyle: '',
      currentTime: '00:00',
      uuid: '0',
      innerLoop: undefined,
      audio: undefined,
      totalDuration: 0,
      hideVolumeSlider: false,
      volumeValue: BASE_VOLUME
    }
  },
  methods: {
    setPosition: function name (e) {
      if (this.paused || e.target.tagName === 'SPAN') {
        return
      }

      let tag = e.target
      const pos = tag.getBoundingClientRect()
      const seekPos = (e.clientX - pos.left) / pos.width
      this.audio.currentTime = parseInt(this.audio.duration * seekPos)
    },
    updateVolume: function () {
      this.hideVolumeSlider = false
      this.audio.volume = this.volumeValue / 100
      if (this.volumeValue / 100 > 0) {
        this.isMuted = this.audio.muted = false
      }

      if (this.volumeValue / 100 === 0) {
        this.isMuted = this.audio.muted = true
      }
    },
    toggleVolume: function () {
      this.hideVolumeSlider = true
    },
    play: function () {
      if (!this.isPaused) return
      this.isPaused = false
      this.audio.play()
      document.activeElement.blur()
    },
    pause: function () {
      this.isPaused = !this.isPaused;
      (this.isPaused) ? this.audio.pause() : this.audio.play()
      document.activeElement.blur()
      this.$emit('pause', this.audio)
    },
    changeLoop: function () {
      this.innerLoop = !this.innerLoop
    },
    download: function () {
      this.stop()
      window.open(this.file, 'download')
    },
    mute: function () {
      this.isMuted = !this.isMuted
      this.audio.muted = this.isMuted
      this.volumeValue = this.isMuted ? 0 : 75
      document.activeElement.blur()
    },
    _handleLoaded: function () {
      if (this.audio.readyState < 2) {
        throw new Error('Failed to load sound file')
      }

      if (this.autoPlay) {
        this.play()
      }

      this.loaded = true
      this.totalDuration = parseInt(this.audio.duration)
    },
    _handlePlayingUI: function (e) {
      let currTime = parseInt(this.audio.currentTime)
      let percentage = parseInt((currTime / this.totalDuration) * 100)
      this.progressStyle = `width:${percentage}%;`
      this.progress = percentage
      this.currentTime = this.convertTimeHHMMSS(currTime)
      document.activeElement.blur()
    },
    _handlePlayPause: function (e) {
      if (e.type === 'pause' && this.isPaused === false) {
        this.progressStyle = 'width:0%;'
        this.currentTime = '00:00'
        this.isPaused = true
      }

      if (e.type === 'play') {
        this.$gtm.trackEvent({
          event: 'user_played_audio',
          category: 'Media',
          action: 'play',
          label: 'User played audio',
          productName: this.bookTitle,
          audioTitle: this.audioTitle,
          isMediaPlayed: 'true'
        })
      }
    },
    _handleEnded: function (e) {
      this.$gtm.trackEvent({
        event: 'user_finished_audio',
        category: 'Media',
        action: 'ended',
        label: 'User finished playing audio',
        productName: this.bookTitle,
        chapterName: this.chapter,
        sectionTitle: this.section,
        audioTitle: this.audioTitle,
        isMediaFinished: 'true'
      })
    },
    init: function () {
      this.audio.addEventListener('timeupdate', this._handlePlayingUI)
      this.audio.addEventListener('loadeddata', this._handleLoaded)
      this.audio.addEventListener('pause', this._handlePlayPause)
      this.audio.addEventListener('play', this._handlePlayPause)
      this.audio.addEventListener('ended', this._handleEnded)
    },
    getAudio: function () {
      return this.$el.querySelectorAll('audio')[0]
    },
    convertTimeHHMMSS: function (val) {
      const formatted = moment('1900-01-01 00:00:00').add(val, 'seconds').format('HH:mm:ss')
      return (formatted.indexOf('00:') === 0) ? formatted.substr(3) : formatted
    }
  },
  mounted: function () {
    this.audio = this.getAudio()
    this.innerLoop = this.loop
    this.init()
  },
  beforeDestroy: function () {
    this.audio.removeEventListener('timeupdate', this._handlePlayingUI)
    this.audio.removeEventListener('loadeddata', this._handleLoaded)
    this.audio.removeEventListener('pause', this._handlePlayPause)
    this.audio.removeEventListener('play', this._handlePlayPause)
    this.audio.removeEventListener('ended', this._handleEnded)
  }
}
</script>

<style lang="scss" scoped>
  $prefixCls: "audio-sound";
  .audio-sound-wrapper {
    width: 100%;
    .audio-sound-player {
      height: 30px;
      line-height: 30px;
      .audio-sound-timer {
        width: 80px;
        float: right;
        font-size: 12px;
        padding-top: 6px;
      }
    }
  }
</style>
